import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, ChevronDown, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Fab } from '@/components/Fab';
import { useAppointments } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { ScopeSwitcher } from '@/components/ScopeSwitcher';
import { AppointmentCard } from '@/components/AppointmentCard';
import { dateISO, fmtDateLong, HHMMtoMin } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useSwipe, useLongPress } from '@/lib/gestures';
import { haptic } from '@/lib/telegram';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuthStore } from '@/store/auth';

type View = 'day' | 'week' | 'month';

const startOfWeek = (d: Date): Date => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

export const CalendarPage = () => {
  const [view, setView] = useState<View>('day');
  const [cursor, setCursor] = useState<Date>(new Date());
  const [quickMenuDate, setQuickMenuDate] = useState<Date | null>(null);
  const navigate = useNavigate();
  const isViewer = useAuthStore((s) => s.master?.role) === 'viewer';

  const range = useMemo(() => {
    if (view === 'day') return { from: dateISO(cursor), to: dateISO(cursor) };
    if (view === 'week') {
      const s = startOfWeek(cursor);
      const e = new Date(s);
      e.setDate(e.getDate() + 6);
      return { from: dateISO(s), to: dateISO(e) };
    }
    const s = startOfMonth(cursor);
    const e = new Date(s.getFullYear(), s.getMonth() + 1, 0);
    return { from: dateISO(s), to: dateISO(e) };
  }, [view, cursor]);

  const { data: appts = [] } = useAppointments(range);

  const shift = (delta: number) => {
    haptic('light');
    const d = new Date(cursor);
    if (view === 'day') d.setDate(d.getDate() + delta);
    if (view === 'week') d.setDate(d.getDate() + delta * 7);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const swipeRef = useSwipe<HTMLDivElement>({
    onLeft: () => shift(1),
    onRight: () => shift(-1),
  });

  const openQuickMenu = (d: Date) => {
    haptic('medium');
    setQuickMenuDate(d);
  };

  const isToday =
    view === 'day' && dateISO(cursor) === dateISO(new Date());

  return (
    <div className="animate-in-up space-y-4 pb-fab" ref={swipeRef}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Календарь</h1>
        <ScopeSwitcher />
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as View)}>
        <TabsList>
          <TabsTrigger value="day">День</TabsTrigger>
          <TabsTrigger value="week">Неделя</TabsTrigger>
          <TabsTrigger value="month">Месяц</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-2">
          <Button variant="ghost" size="icon-sm" onClick={() => shift(-1)} aria-label="Назад">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <button
            onClick={() => setCursor(new Date())}
            className="flex-1 text-center"
            title="К сегодня"
          >
            <div className="text-sm font-bold capitalize">
              {view === 'day' && fmtDateLong(cursor)}
              {view === 'week' && weekLabel(cursor)}
              {view === 'month' &&
                cursor.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
            </div>
            {!isToday && (
              <div className="text-[10px] uppercase tracking-wider text-primary">
                Нажмите для «сегодня»
              </div>
            )}
          </button>
          <Button variant="ghost" size="icon-sm" onClick={() => shift(1)} aria-label="Вперёд">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </Card>

      {view === 'day' && <DayView date={cursor} appts={appts} onLong={openQuickMenu} />}
      {view === 'week' && (
        <WeekView
          start={startOfWeek(cursor)}
          appts={appts}
          onPick={(d) => {
            setView('day');
            setCursor(d);
          }}
          onLong={openQuickMenu}
        />
      )}
      {view === 'month' && (
        <MonthView
          cursor={cursor}
          appts={appts}
          onPick={(d) => {
            setView('day');
            setCursor(d);
          }}
          onLong={openQuickMenu}
        />
      )}

      {!isViewer && <Fab to="/appointments/new" label="Новая запись" />}

      <Sheet open={!!quickMenuDate} onOpenChange={(o) => !o && setQuickMenuDate(null)}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle className="capitalize">
              {quickMenuDate && fmtDateLong(quickMenuDate)}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-2.5 pt-2">
            {!isViewer && (
              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  if (!quickMenuDate) return;
                  navigate(`/appointments/new?date=${dateISO(quickMenuDate)}`);
                  setQuickMenuDate(null);
                }}
              >
                <Plus className="h-5 w-5" /> Новая запись
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={() => {
                if (!quickMenuDate) return;
                setView('day');
                setCursor(quickMenuDate);
                setQuickMenuDate(null);
              }}
            >
              <CalendarPlus className="h-4 w-4" /> Открыть день
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const weekLabel = (d: Date) => {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return `${s.getDate()} ${s.toLocaleDateString('ru', { month: 'short' })} – ${e.getDate()} ${e.toLocaleDateString('ru', { month: 'short' })}`;
};

const DayView = ({
  date,
  appts,
  onLong,
}: {
  date: Date;
  appts: any[];
  onLong: (d: Date) => void;
}) => {
  const ds = dateISO(date);
  const dayAppts = appts.filter((a) => dateISO(new Date(a.date)) === ds);
  const longPress = useLongPress({ onLongPress: () => onLong(date) });
  const hours = Array.from({ length: 16 }, (_, i) => i + 7); // 7:00–22:00
  return (
    <div className="space-y-3" {...longPress}>
      <div className="space-y-2">
        {dayAppts.map((a) => (
          <AppointmentCard key={a.id} appointment={a} />
        ))}
        {!dayAppts.length && (
          <Card>
            <div className="flex flex-col items-center gap-2 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <CalendarPlus className="h-5 w-5" />
              </div>
              <div className="text-sm font-medium">Записей нет</div>
              <div className="text-xs text-muted-foreground">
                Удерживайте дату, чтобы быстро создать
              </div>
            </div>
          </Card>
        )}
      </div>

      <details className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
        <summary className="flex cursor-pointer items-center justify-between p-4 text-sm font-semibold">
          <span>Таймлайн дня</span>
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t border-border/60 px-3 py-2">
          {hours.map((h) => (
            <div key={h} className="relative flex h-14 items-start gap-3 border-t border-dashed border-border/60 pt-1 first:border-t-0">
              <div className="w-10 shrink-0 pt-0.5 text-[11px] font-medium tabular text-muted-foreground">
                {String(h).padStart(2, '0')}:00
              </div>
              <div className="relative flex-1 space-y-1">
                {dayAppts
                  .filter((a) => {
                    const s = HHMMtoMin(a.start_time);
                    return s >= h * 60 && s < (h + 1) * 60;
                  })
                  .map((a) => (
                    <Link
                      key={a.id}
                      to={`/appointments/${a.id}`}
                      className="block truncate rounded-lg border-l-[3px] px-2.5 py-1 text-xs font-semibold transition-all hover:translate-x-0.5"
                      style={{
                        background: `${a.service?.color}1a`,
                        color: a.service?.color,
                        borderLeftColor: a.service?.color,
                      }}
                    >
                      <span className="tabular">{a.start_time}</span>{' '}
                      <span className="text-foreground">{a.client?.name}</span>
                    </Link>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

const WeekView = ({
  start,
  appts,
  onPick,
  onLong,
}: {
  start: Date;
  appts: any[];
  onPick: (d: Date) => void;
  onLong: (d: Date) => void;
}) => {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
  const today = dateISO(new Date());
  return (
    <div className="space-y-3">
      {days.map((d) => {
        const ds = dateISO(d);
        const list = appts.filter((a) => dateISO(new Date(a.date)) === ds);
        return (
          <WeekDay key={ds} d={d} list={list} today={today} onPick={onPick} onLong={onLong} />
        );
      })}
    </div>
  );
};

const WeekDay = ({
  d,
  list,
  today,
  onPick,
  onLong,
}: {
  d: Date;
  list: any[];
  today: string;
  onPick: (d: Date) => void;
  onLong: (d: Date) => void;
}) => {
  const ds = dateISO(d);
  const lp = useLongPress({ onLongPress: () => onLong(d) });
  const isTodayCell = ds === today;
  const weekday = d.toLocaleDateString('ru', { weekday: 'short' });
  return (
    <div onClick={() => onPick(d)} {...lp} className="rounded-2xl">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-9 w-9 flex-col items-center justify-center rounded-xl text-xs font-bold',
              isTodayCell
                ? 'bg-brand text-white shadow-soft'
                : 'bg-secondary text-foreground',
            )}
          >
            <span className="text-[9px] font-medium uppercase opacity-80">{weekday}</span>
            <span className="text-sm leading-none">{d.getDate()}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {d.toLocaleDateString('ru', { month: 'long' })}
          </span>
        </div>
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {list.length ? `${list.length} зап.` : 'свободно'}
        </span>
      </div>
      {list.length ? (
        <div className="space-y-1">
          {list.map((a) => (
            <Card
              key={a.id}
              className="px-3 py-2 transition-all hover:-translate-x-0.5"
              style={{ borderLeft: `3px solid ${a.service?.color}` }}
            >
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="tabular font-bold">{a.start_time}</span>
                <span className="truncate flex-1 font-medium">{a.client?.name}</span>
                <span className="truncate text-xs text-muted-foreground">{a.service?.name}</span>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const MonthView = ({
  cursor,
  appts,
  onPick,
  onLong,
}: {
  cursor: Date;
  appts: any[];
  onPick: (d: Date) => void;
  onLong: (d: Date) => void;
}) => {
  const first = startOfMonth(cursor);
  const startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
  }
  const today = dateISO(new Date());
  return (
    <Card className="p-3">
      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = dateISO(d);
          const list = appts.filter((a) => dateISO(new Date(a.date)) === ds);
          const colors = Array.from(new Set(list.map((a) => a.service?.color))).slice(0, 4);
          return (
            <MonthCell
              key={ds}
              d={d}
              colors={colors}
              isToday={ds === today}
              onPick={onPick}
              onLong={onLong}
            />
          );
        })}
      </div>
    </Card>
  );
};

const MonthCell = ({
  d,
  colors,
  isToday,
  onPick,
  onLong,
}: {
  d: Date;
  colors: Array<string | undefined>;
  isToday: boolean;
  onPick: (d: Date) => void;
  onLong: (d: Date) => void;
}) => {
  const lp = useLongPress({ onLongPress: () => onLong(d) });
  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
  return (
    <button
      onClick={() => onPick(d)}
      {...lp}
      className={cn(
        'flex aspect-square flex-col items-center justify-between rounded-xl p-1.5 text-sm transition-all',
        'hover:bg-secondary',
        isToday
          ? 'bg-brand text-white font-bold shadow-soft hover:bg-brand'
          : isWeekend
          ? 'text-muted-foreground'
          : 'text-foreground',
      )}
    >
      <span className={cn('text-sm', isToday && 'font-bold')}>{d.getDate()}</span>
      <div className="flex gap-0.5">
        {colors.map((c, idx) => (
          <span
            key={idx}
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: isToday ? '#fff' : (c ?? '#999') }}
          />
        ))}
      </div>
    </button>
  );
};
